import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Modal from "react-modal";
import { ButtonSkeleton } from "./CalendarComponents/SkeletonLoader.jsx";
import { toast } from "react-toastify";
import { DatePicker } from "rsuite";
import moment from "moment";
import CalendarIcon from '../ui/CalendarIcon';

const REPEAT_TYPES = [
    { value: "does_not_repeat", label: "Does Not Repeat" },
    { value: "annually", label: "Annually" },
    { value: "variable", label: "Variable (e.g., 4th Thursday)" },
];

const ranges = [
    {
        label: "Today",
        value: [new Date(), new Date()],
    },
    {
        label: "Tomorrow",
        value: [moment().add(1, "days").toDate(), moment().add(1, "days").toDate()],
    },
    {
        label: "Next 7 Days",
        value: [new Date(), moment().add(6, "days").toDate()],
    },
];

const EditHolidayModal = ({
    editHolidayModalOpen,
    setEditHolidayModalOpen,
    isSubmitLoading,
    setIsSubmitLoading,
    selectedHolidayForEdit,
    setSelectedHolidayForEdit,
    setSelectedEvents,
    setExpandedEventIndex,
    setModalIsOpen,
    user,
    refetchHolidays,
    token,
    userProfiles,
}) => {
    const [holidayName, setHolidayName] = useState("");
    const [repeatType, setRepeatType] = useState("does_not_repeat");
    const [holidayDate, setHolidayDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);
    const [description, setDescription] = useState("");
    const [holidayNameIsValid, setHolidayNameIsValid] = useState(false);
    const [holidayDateIsValid, setHolidayDateIsValid] = useState(false);
    const [endDateIsValid, setEndDateIsValid] = useState(false);
    const [recurrenceEndDateIsValid, setRecurrenceEndDateIsValid] = useState(false);
    const [repeatTypeIsValid, setRepeatTypeIsValid] = useState(false);
    const [repeatTypeDropdownOpen, setRepeatTypeDropdownOpen] = useState(false);
    const [repeatRule, setRepeatRule] = useState("");
    const [repeatRuleIsValid, setRepeatRuleIsValid] = useState(false);
    const [appliesToIsValid, setAppliesToIsValid] = useState(false);
    const [appliesTo, setAppliesTo] = useState([]);
    const [appliesToInput, setAppliesToInput] = useState("");
    const [appliesToSuggestions, setAppliesToSuggestions] = useState([]);
    const [isAppliesToDropdownOpen, setIsAppliesToDropdownOpen] = useState(false);
    const [selectAllCountries, setSelectAllCountries] = useState(false);
    const [isHoliday, setIsHoliday] = useState(true);
    const [updateScope, setUpdateScope] = useState('this');

    const holidayNameRef = useRef(null);
    const repeatTypeRef = useRef(null);
    const holidayDateRef = useRef(null);
    const endDateRef = useRef(null);
    const recurrenceEndDateRef = useRef(null);
    const repeatRuleRef = useRef(null);
    const appliesToRef = useRef(null);

    const uniqueCountries = [...new Set(userProfiles.map(user => user.country || "Unknown"))];
    const isRecurring = selectedHolidayForEdit?.repeatType && selectedHolidayForEdit.repeatType !== "does_not_repeat" ? true : false;
    useEffect(() => {
        if (selectedHolidayForEdit) {
            setHolidayName(selectedHolidayForEdit.title || "");
            setHolidayDate(selectedHolidayForEdit.start || null);
            // const adjustedEndDate = selectedHolidayForEdit.end ? moment(selectedHolidayForEdit.end).subtract(1, 'days').toDate() : null;
            // setEndDate(moment(selectedHolidayForEdit.start).isSame(moment(selectedHolidayForEdit.end).subtract(1, 'days'), 'day') ? null : adjustedEndDate);
            const adjustedEndDate = selectedHolidayForEdit.end ? moment(selectedHolidayForEdit.end).subtract(1, 'days').toDate() : null;
            // setEndDate(selectedHolidayForEdit.repeatType === "variable" ? null : moment(selectedHolidayForEdit.start).isSame(moment(selectedHolidayForEdit.end).subtract(1, 'days'), 'day') ? null : adjustedEndDate);
            setEndDate(
                selectedHolidayForEdit.repeatType === "variable" ||
                    moment(selectedHolidayForEdit.start).isSame(moment(selectedHolidayForEdit.end).subtract(1, 'days'), 'day')
                    ? selectedHolidayForEdit.start
                    : adjustedEndDate
            );
            const adjustedRecurrenceEndDate = selectedHolidayForEdit.recurrenceEndDate
                ? moment(selectedHolidayForEdit.recurrenceEndDate).subtract(1, 'day').toDate()
                : null;
            setRecurrenceEndDate(adjustedRecurrenceEndDate || null);
            setRepeatType(selectedHolidayForEdit.repeatType || "does_not_repeat");
            setRepeatRule(selectedHolidayForEdit.repeatRule || "");
            setAppliesTo(selectedHolidayForEdit.appliesTo || []);
            setIsHoliday(selectedHolidayForEdit.isHoliday !== undefined ? selectedHolidayForEdit.isHoliday : true);
            setDescription(selectedHolidayForEdit.description || "");
            setUpdateScope('this');
        }
    }, [selectedHolidayForEdit]);

    useEffect(() => {
        if (repeatType === "variable" && !repeatRule) {
            setRepeatRuleIsValid(true);
        } else {
            setRepeatRuleIsValid(false);
        }
    }, [repeatType, repeatRule]);

    useEffect(() => {
        if (!appliesToInput) return setAppliesToSuggestions([]);

        const selectedIds = appliesTo.map(a => a._id);
        const filtered = userProfiles.filter((user) =>
            user.email.toLowerCase().includes(appliesToInput.toLowerCase()) &&
            !selectedIds.includes(user._id)
        );

        setAppliesToSuggestions(filtered);
    }, [appliesToInput, userProfiles, appliesTo]);

    const handleSelectAppliesTo = (user) => {
        if (!appliesTo.some((a) => a._id === user._id)) {
            setAppliesTo([...appliesTo, user]);
        }
        setAppliesToInput("");
        setAppliesToSuggestions([]);
    };

    const handleRemoveAppliesTo = (id) => {
        setAppliesTo(appliesTo.filter((a) => a._id !== id));
    };

    const handleSelectAllCountries = () => {
        setSelectAllCountries(!selectAllCountries);
        if (!selectAllCountries) {
            const allUserIds = userProfiles.map(user => user._id);
            setAppliesTo(userProfiles.filter(user => allUserIds.includes(user._id)));
        } else {
            setAppliesTo([]);
        }
    };

    const handleCheckboxChange = (country) => {
        const usersInCountry = userProfiles.filter(user => user.country === country);
        const userIds = usersInCountry.map(user => user._id);
        const currentAppliesToIds = new Set(appliesTo.map(user => user._id));

        let updatedAppliesTo;
        if (userIds.every(id => currentAppliesToIds.has(id))) {
            updatedAppliesTo = appliesTo.filter(user => !userIds.includes(user._id));
        } else {
            updatedAppliesTo = [
                ...appliesTo.filter(user => !userIds.includes(user._id)),
                ...usersInCountry.filter(user => !currentAppliesToIds.has(user._id))
            ];
        }
        setAppliesTo(updatedAppliesTo);
    };

    const toggleAppliesToDropdown = () => {
        setIsAppliesToDropdownOpen(!isAppliesToDropdownOpen);
    };

    const selectedCountries = [
        ...new Set(
            appliesTo
                .map(a => userProfiles.find(user => user._id === a._id)?.country)
                .filter(Boolean)
        )
    ];

    const handleToggleRepeatTypeDropdown = () => {
        setRepeatTypeDropdownOpen(!repeatTypeDropdownOpen);
    };

    const handleHolidayDateSelect = (date) => {
        setHolidayDate(date);
        setHolidayDateIsValid(false);
        if (recurrenceEndDate && moment(recurrenceEndDate).isBefore(moment(date), "day")) {
            setRecurrenceEndDate(null);
        }
    };

    const handleRecurrenceEndDateSelect = (date) => {
        setRecurrenceEndDate(date);
        setRecurrenceEndDateIsValid(false);
    };

    const handleCloseEditHolidayModal = () => {
        if (!isSubmitLoading) {
            setEditHolidayModalOpen(false);
            setSelectedHolidayForEdit(null);
            setHolidayName("");
            setHolidayDate(null);
            setEndDate(null);
            setRecurrenceEndDate(null);
            setRepeatType("does_not_repeat");
            setRepeatRule("");
            setAppliesTo([]);
            setIsHoliday(true);
            setDescription("");
            setHolidayNameIsValid(false);
            setHolidayDateIsValid(false);
            setEndDateIsValid(false);
            setRecurrenceEndDateIsValid(false);
            setRepeatTypeIsValid(false);
            setRepeatRuleIsValid(false);
            setAppliesToIsValid(false);
            setRepeatTypeDropdownOpen(false);
            setIsAppliesToDropdownOpen(false);
            setUpdateScope('this');
            document.body.classList.remove("ReactModal__Body--open");
        }
    };

    const handleEditHolidaySubmit = async (e) => {
        e.preventDefault();

        setHolidayNameIsValid(false);
        setHolidayDateIsValid(false);
        setEndDateIsValid(false);
        setRecurrenceEndDateIsValid(false);
        setRepeatTypeIsValid(false);
        setRepeatRuleIsValid(false);
        setAppliesToIsValid(false);

        let isValid = true;

        if (!holidayName) {
            setHolidayNameIsValid(true);
            holidayNameRef.current?.focus();
            isValid = false;
        }
        if (!holidayDate) {
            setHolidayDateIsValid(true);
            holidayDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (!repeatType) {
            setRepeatTypeIsValid(true);
            repeatTypeRef.current?.focus();
            isValid = false;
        }
        if (repeatType === "variable" && !repeatRule) {
            setRepeatRuleIsValid(true);
            repeatRuleRef.current?.focus();
            isValid = false;
        }
        if (isRecurring && !updateScope) {
            toast.error("Please select update scope!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            isValid = false;
        }
        if (repeatType !== "variable" && endDate && moment(endDate).isBefore(moment(holidayDate), "day")) {
            toast.error("End date must be on or after holiday date!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            setEndDateIsValid(true);
            endDateRef.current?.focus();
            isValid = false;
        }
        if (repeatType === "variable" && recurrenceEndDate && moment(recurrenceEndDate).isBefore(moment(holidayDate), "day")) {
            toast.error("Recurrence end date must be on or after holiday date!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            setRecurrenceEndDateIsValid(true);
            recurrenceEndDateRef.current?.focus();
            isValid = false;
        }
        if (appliesTo.length === 0) {
            setAppliesToIsValid(true);
            isValid = false;
        }

        if (!isValid) {
            toast.error("Please fill all required fields!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        try {
            setIsSubmitLoading(true);
            const requestData = {
                holiday_name: holidayName,
                holiday_date: moment(holidayDate).format("YYYY-MM-DD"),
                end_date: repeatType !== "variable" && endDate ? moment(endDate).format("YYYY-MM-DD") : null,
                recurrence_end_date: repeatType === "variable" && recurrenceEndDate ? moment(recurrenceEndDate).format("YYYY-MM-DD") : null,
                repeat_type: repeatType,
                repeat_rule: repeatType === "variable" ? repeatRule : null,
                applies_to: appliesTo.length > 0 ? appliesTo.map((a) => a._id) : [],
                is_holiday: isHoliday,
                description: description || null,
                appId: user.appId,
                update_scope: updateScope,
                // timeZone: "Asia/Kolkata",
            };

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            await axios.put(`/api/holidays/update/${selectedHolidayForEdit.holidayId}`, requestData, config);

            toast.success("Holiday updated successfully", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            refetchHolidays();
            handleCloseEditHolidayModal();
            setModalIsOpen(false);
            setExpandedEventIndex(null);
            setSelectedEvents([]);
        } catch (error) {
            console.error("Error updating holiday:", error);
            toast.error(error.response?.data?.message || "Something went wrong", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleClickOutsideDropdown = (event) => {
        const refs = { repeatType: repeatTypeRef.current, appliesTo: appliesToRef.current };
        if (refs.repeatType && !refs.repeatType.contains(event.target)) {
            setRepeatTypeDropdownOpen(false);
        }
        if (refs.appliesTo && !refs.appliesTo.contains(event.target)) {
            setIsAppliesToDropdownOpen(false);
        }
    };

    useEffect(() => {
        if (typeof window !== "undefined" && document) {
            document.addEventListener("click", handleClickOutsideDropdown);
            return () => document.removeEventListener("click", handleClickOutsideDropdown);
        }
    }, []);

    return (
        <Modal
            className={`client-modal sprint-modal time-off-close calendar-event-create calendar-holiday-create calendar-event-edit ${repeatType === "variable" ? "recurrenceDate" : ""}`}
            isOpen={editHolidayModalOpen}
            onRequestClose={!isSubmitLoading ? handleCloseEditHolidayModal : undefined}
            contentLabel="Edit Holiday"
            style={{
                content: {
                    height: "45%",
                    width: "30%",
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                },
            }}
        >
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                    <div className="modal timeoff-modal">
                        <div>
                            <div className="modal-client-head">
                                <h2>{isRecurring ? "Edit Recurring Holiday" : "Edit Holiday"}</h2>
                                <div className="modal-close-invite outter-modal-main">
                                    <a onClick={handleCloseEditHolidayModal} disabled={isSubmitLoading}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                        >
                                            <path d="M1 13L13 1" stroke="black" />
                                            <path d="M1 0.999999L13 13" stroke="black" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                            <div className="form-field-client-outer">
                                <form onSubmit={handleEditHolidaySubmit}>
                                    <div className="form-field-client">
                                        <label htmlFor="holidayName" className="form-label">
                                            Holiday Name *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="holidayName"
                                                type="text"
                                                value={holidayName}
                                                onChange={(e) => setHolidayName(e.target.value)}
                                                className={holidayNameIsValid ? "input-validation-error form-input" : "form-input"}
                                                ref={holidayNameRef}
                                                placeholder="Enter the name for holiday"
                                            />
                                            {holidayNameIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Holiday name is required
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-field-client">
                                        <label htmlFor="holidayDate" className="form-label">
                                            Start Date *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div className="date-picker-outer date-picker-outer-all">
                                                <div className="custoom-icon-calender custoom-icon-calender-all">
                                                    <div ref={holidayDateRef}>
                                                        <DatePicker
                                                            oneTap
                                                            format="dd/MM/yyyy"
                                                            placeholder="Select Date"
                                                            placement="auto"
                                                            ranges={ranges}
                                                            className="project-calender time-off-cal"
                                                            value={holidayDate}
                                                            onChange={handleHolidayDateSelect}
              caretAs={CalendarIcon}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {holidayDateIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Select the holiday date
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {repeatType !== "variable" ? (
                                        <div className="form-field-client">
                                            <label htmlFor="endDate" className="form-label">
                                                End Date
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div className="date-picker-outer date-picker-outer-all">
                                                    <div className="custoom-icon-calender custoom-icon-calender-all">
                                                        <div ref={endDateRef}>
                                                            <DatePicker
                                                                oneTap
                                                                format="dd/MM/yyyy"
                                                                placeholder="Select End Date"
                                                                placement="topEnd"
                                                                ranges={ranges}
                                                                className="project-calender time-off-cal"
                                                                value={endDate}
                                                                onChange={(date) => setEndDate(date)}
                                                                // timeZone="Asia/Kolkata"
                                                                shouldDisableDate={(date) => moment(date).isBefore(moment(holidayDate), "day")}
              caretAs={CalendarIcon}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {endDateIsValid && (
                                                    <span className="text-red-500 text-xs italic">
                                                        End date must be on or after holiday date
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="form-field-client">
                                            <label htmlFor="recurrenceEndDate" className="form-label">
                                                Recurrence End Date
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div className="date-picker-outer date-picker-outer-all">
                                                    <div className="custoom-icon-calender custoom-icon-calender-all">
                                                        <div ref={recurrenceEndDateRef}>
                                                            <DatePicker
                                                                oneTap
                                                                format="dd/MM/yyyy"
                                                                placeholder="Select Recurrence End Date"
                                                                placement="auto"
                                                                ranges={ranges}
                                                                className="project-calender time-off-cal"
                                                                value={recurrenceEndDate}
                                                                onChange={handleRecurrenceEndDateSelect}
                                                                shouldDisableDate={(date) => moment(date).isBefore(moment(holidayDate), "day")}
                                                                disabled={isRecurring && updateScope === 'this'}
              caretAs={CalendarIcon}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {recurrenceEndDateIsValid && (
                                                    <span className="text-red-500 text-xs italic">
                                                        Recurrence end date must be on or after holiday date
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {isRecurring ? (
                                        <div className="form-field-client scope-sec">
                                            <label className="form-label">Update Scope *</label>
                                            <div className="inner-sec-cal-form">
                                                <div className="radio-group">
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            value="this"
                                                            checked={updateScope === 'this'}
                                                            onChange={() => setUpdateScope('this')}
                                                            className="form-radio"
                                                        />
                                                        This Event
                                                    </label>
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            value="thisAndFollowing"
                                                            checked={updateScope === 'thisAndFollowing'}
                                                            onChange={() => setUpdateScope('thisAndFollowing')}
                                                            className="form-radio"
                                                        />
                                                        This and Following Events
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                    <div className="form-field-client">
                                        <label htmlFor="repeatType" className="form-label">
                                            Repeat *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            {(isRecurring && updateScope === 'this') ? (
                                                <span className={`custom-dropdown-user-new custom-dropdown-select disabled ${repeatTypeIsValid ? "input-validation-error" : ""}`}>
                                                    {REPEAT_TYPES.find((rt) => rt.value === repeatType)?.label || "Select"}
                                                </span>
                                            ) : (
                                                <div ref={repeatTypeRef} className={`cursor ${repeatTypeDropdownOpen ? "open" : ""}`}>
                                                    <span
                                                        id="repeat-type-select"
                                                        onClick={handleToggleRepeatTypeDropdown}
                                                        className={`custom-dropdown-user-new custom-dropdown-select ${repeatTypeIsValid ? "input-validation-error" : ""}`}
                                                    >
                                                        {REPEAT_TYPES.find((rt) => rt.value === repeatType)?.label || "Select"}
                                                    </span>
                                                    {repeatTypeDropdownOpen && (
                                                        <div className="custom-dropdown-new">
                                                            <ul className="leave-type-list">
                                                                {REPEAT_TYPES.map((rt) => (
                                                                    <li
                                                                        key={rt.value}
                                                                        className={repeatType === rt.value ? "selected-item" : ""}
                                                                        onClick={() => {
                                                                            setRepeatType(rt.value);
                                                                            setRepeatTypeDropdownOpen(false);
                                                                            if (rt.value !== "variable") {
                                                                                setRepeatRule("");
                                                                                setRecurrenceEndDate(null);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {rt.label}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {repeatTypeIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Repeat type is required
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {repeatType === "variable" && (
                                        <div className="form-field-client">
                                            <label htmlFor="repeatRule" className="form-label">
                                                Repeat Rule *
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <input
                                                    id="repeatRule"
                                                    type="text"
                                                    value={repeatRule}
                                                    onChange={(e) => (!isRecurring || updateScope !== 'this') && setRepeatRule(e.target.value)}  // Only allow change if not recurring
                                                    className={repeatRuleIsValid ? "input-validation-error form-input" : "form-input" + ((updateScope === 'this') ? " disabled" : "")}
                                                    ref={repeatRuleRef}
                                                    placeholder="e.g., 4th Thursday of November"
                                                    disabled={isRecurring && updateScope === 'this'}
                                                />
                                                {repeatRuleIsValid && (
                                                    <span className="text-red-500 text-xs italic">
                                                        Repeat rule is required for variable holidays
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-field-client">
                                        <label htmlFor="appliesTo" className="form-label">
                                            Applies To *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={appliesToRef} className={`cursor ${isAppliesToDropdownOpen ? "open" : ""}`}>
                                                <span
                                                    id="appliesTo"
                                                    onClick={toggleAppliesToDropdown}
                                                    className={`custom-dropdown-user-new custom-dropdown-select ${appliesToIsValid ? "input-validation-error" : ""}`}
                                                >
                                                    {appliesTo.length === userProfiles.length ? (
                                                        "All"
                                                    ) : (
                                                        <>
                                                            {userProfiles.find((user) => user._id === appliesTo[0]?._id)?.country || "Select"}
                                                            {selectedCountries.length > 1 && (
                                                                <div className="selected-option-count">
                                                                    {selectedCountries.length - 1}+
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </span>
                                                {isAppliesToDropdownOpen && (
                                                    <div className="custom-dropdown-new" role="listbox">
                                                        <ul className="leave-type-list">
                                                            {uniqueCountries.length === 0 ? (
                                                                <li>No countries available</li>
                                                            ) : (
                                                                <>
                                                                    <li>
                                                                        <label className="checkbox-label1">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectAllCountries}
                                                                                onChange={handleSelectAllCountries}
                                                                            />
                                                                            All Countries
                                                                        </label>
                                                                    </li>
                                                                    {uniqueCountries.map((country) => (
                                                                        <li key={country}>
                                                                            <label className="checkbox-label1" key={country}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={userProfiles.filter(user => user.country === country).every(user => appliesTo.some(a => a._id === user._id))}
                                                                                    onChange={() => handleCheckboxChange(country)}
                                                                                />
                                                                                {country}
                                                                            </label>
                                                                        </li>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {appliesToIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Applies to is required
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-field-client">
                                        <label htmlFor="description" className="form-label">
                                            Description
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <textarea
                                                id="description"
                                                name="description"
                                                className="form-textarea"
                                                rows="3"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                placeholder="Write here"
                                            ></textarea>
                                        </div>
                                    </div>
                                    <button type="submit" disabled={isSubmitLoading} className="submit-client submit-client-cal">
                                        {isSubmitLoading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <ButtonSkeleton width="110px" height="18px" />
                                            </div>
                                        ) : (
                                            "Update Holiday"
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EditHolidayModal;